import axios from 'axios'

// --- Enums ---

/**
 * Supported languages for Swikly API requests
 */
export enum SwiklyLanguage {
  FR = 'fr',
  EN = 'en',
  ES = 'es',
  IT = 'it',
  NL = 'nl',
  DE = 'de',
}

// --- Interfaces for Swikly API ---

/**
 * Payload for creating a new Swikly request
 * Contains all the data needed to create a deposit, payment, or no-show request
 */
export interface CreateRequestPayload {
  description: string
  language: SwiklyLanguage
  customId?: string // Custom id related to your business
  customIdMustBeUnique?: boolean
  skipToPaymentPageIfPossible?: boolean
  freeText?: string
  firstName?: string
  lastName?: string
  email?: string
  phoneNumber?: string
  birthDate?: string // ISO date format
  /**
   * Deprecated, use callbacks object instead
   */
  callbackUrl?: string
  redirectUrl?: string
  returnUrl?: string
  sendEmail?: boolean
  sendSms?: boolean
  partnerTag?: string
  callbacks?: {
    requestSecured?: string
    allPendingReclaimsCompleted?: string
    allPendingRefundsCompleted?: string
  }
  deposit?: {
    startDate: string // ISO date format
    endDate: string // ISO date format
    amount: number // Amount in cents
  }
  noShow?: {
    date: string // ISO date format
    amount: number // Amount in cents
  }
  payment?: {
    dueDate?: string // ISO date format
    amount: number // Amount in cents
  }
  address?: any // Address object
  // Deprecated fields
  /**
   * Deprecated, use deposit.startDate
   */
  startDate?: string
  /**
   * Deprecated, use deposit.endDate
   */
  endDate?: string
  /**
   * Deprecated, use deposit.amount
   */
  depositAmount?: number
  /**
   * Deprecated, use noShow.amount
   */
  noShowAmount?: number
  /**
   * Deprecated, use payment.amount
   */
  paymentAmount?: number
}

/**
 * Success response from POST /accounts/{accountId}/requests
 */
export interface CreateRequestResponse {
  request: Request
}

/**
 * Main Request object returned by Swikly API
 * Contains all details about a Swikly request including deposit, payment, and no-show information
 */
export interface Request {
  id: string
  accountId: string
  legacyId?: string
  customId?: string
  customIdMustBeUnique?: boolean
  skipToPaymentPageIfPossible?: boolean
  lastName?: string
  firstName?: string
  email?: string
  phoneNumber?: string
  description: string
  freeText?: string
  releasable: boolean
  cancelable: boolean
  deposit?: Deposit
  noShow?: NoShow
  payment?: Payment
  endUser?: any
  link: string
  redirectUrl?: any
  returnUrl?: any
  createdAt: string
}

/**
 * Deposit object from the Swikly API
 * Represents a security deposit that can be reclaimed
 */
export interface Deposit {
  id: string
  requestId: string
  request?: any
  amount: number // Amount in cents
  amountToBeSecured: number // Amount in cents
  securedAmount: number // Amount in cents
  status:
    | 'Pending'
    | 'Temporary'
    | 'Canceled'
    | 'Initialized'
    | 'Accepted'
    | 'Failed'
    | 'Released'
    | 'Expired'
    | 'ExpiredWithoutAcceptance'
  startDate: string // ISO date format
  endDate: string // ISO date format
  expirationDate: string // ISO date format
  reclaimSummary?: any
  refundSummary?: any
  acceptedAt?: string // ISO date-time format
  releasedAt?: string // ISO date-time format
  canceledAt?: string // ISO date-time format
  createdAt: string // ISO date-time format
}

/**
 * NoShow object from the Swikly API
 * Represents a no-show deposit for missed appointments
 */
export interface NoShow {
  id: string
  requestId: string
  request?: any
  amount: number // Amount in cents
  amountToBeSecured: number // Amount in cents
  securedAmount: number // Amount in cents
  status:
    | 'Pending'
    | 'Temporary'
    | 'Canceled'
    | 'Initialized'
    | 'Accepted'
    | 'Failed'
    | 'Released'
    | 'Expired'
    | 'ExpiredWithoutAcceptance'
  reservationDate: string // ISO date format
  expirationDate: string // ISO date format
  reclaimSummary?: any
  refundSummary?: any
  acceptedAt?: string // ISO date-time format
  releasedAt?: string // ISO date-time format
  canceledAt?: string // ISO date-time format
  createdAt: string // ISO date-time format
}

/**
 * Payment object from the Swikly API
 * Represents a payment that can be refunded
 */
export interface Payment {
  id: string
  requestId: string
  request?: any
  amount: number // Amount in cents
  amountToBePaid: number // Amount in cents
  paidAmount: number // Amount in cents
  status:
    | 'Pending'
    | 'Temporary'
    | 'Canceled'
    | 'Initialized'
    | 'Accepted'
    | 'Failed'
    | 'Refunded'
  dueDate?: string // ISO date format
  refundSummary?: any
  acceptedAt?: string // ISO date-time format
  refundedAt?: string // ISO date-time format
  canceledAt?: string // ISO date-time format
  createdAt: string // ISO date-time format
}

/**
 * Recipient data in Swikly responses
 */
export interface RequestRecipient {
  firstName: string
  lastName: string
  phoneNumber: string
  email: string
}

/**
 * Success response from GET /accounts/{accountId}/requests/{id}
 */
export interface GetRequestResponse {
  request: Request
}

// --- Frontend Service Functions ---

/**
 * Get the status of a Swikly caution by its ID
 * @param swiklyId - The Swikly request ID
 * @returns Promise with the request details including deposit status
 */
export const getSwiklyCautionStatus = async (
  swiklyId: string
): Promise<GetRequestResponse> => {
  const response = await axios.get(`/api/swikly/${swiklyId}`)
  return response.data
}

/**
 * Create a new Swikly caution (deposit)
 * @param data - The caution creation payload
 * @returns Promise with the created request details
 */
export const createSwiklyCaution = async (
  data: CreateRequestPayload
): Promise<CreateRequestResponse> => {
  const response = await axios.post('/api/swikly/create', data)
  return response.data
}

// Note: Reclaim and cancel endpoints were removed as they are not currently used
// If needed in the future, they can be re-implemented with proper UI components
